import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { showNotification, showError } from '../utils/notificationHandler';

export default function ResetPassword() {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [tokenValid, setTokenValid] = useState(null);
  const [resetComplete, setResetComplete] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const captchaRef = useRef(null);
  const history = useHistory();
  const hasRunTokenCheck = useRef(false); // Guard against React StrictMode double-execution

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Check if we have valid reset parameters
    const checkResetToken = async () => {
      // Prevent double-execution in React StrictMode
      if (hasRunTokenCheck.current) {
        console.log('Token check already run, skipping (React StrictMode guard)');
        return;
      }
      hasRunTokenCheck.current = true;

      // Check for errors in URL first
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const error = urlParams.get('error') || hashParams.get('error');
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');

      if (error) {
        console.error('Reset password error from URL:', error, errorDescription);
        setTokenValid(false);
        return;
      }

      try {
        // PRIORITY 1: Check for existing session (from recovery link via generateLink)
        // Supabase's generateLink() creates the session server-side before redirecting
        console.log('Checking for existing recovery session...');
        const { data: { session: existingSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (!sessionError && existingSession && existingSession.user) {
          console.log('Found existing recovery session for user:', existingSession.user.id);
          setTokenValid(true);
          return;
        }

        // PRIORITY 2: Check for PKCE code parameter (modern flow)
        const code = urlParams.get('code');
        if (code) {
          console.log('Using PKCE code exchange for recovery link');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          
          if (exchangeError) {
            console.error('Code exchange error:', exchangeError);
            setTokenValid(false);
            return;
          }
          
          if (data.session && data.user) {
            console.log('Recovery session established successfully via PKCE for user:', data.user.id);
            setTokenValid(true);
            return;
          }
        }

        // PRIORITY 3: Check for hash-based tokens (legacy flow)
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        if (accessToken && refreshToken && type === 'recovery') {
          console.log('Using legacy hash-based token flow');
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            console.error('Session setup error:', sessionError);
            setTokenValid(false);
            return;
          }

          if (data.session && data.user) {
            console.log('Recovery session established successfully for user:', data.user.id);
            setTokenValid(true);
            return;
          }
        }

        // NO VALID SESSION OR TOKENS FOUND
        console.error('No valid recovery session or tokens found');
        setTokenValid(false);
      } catch (error) {
        console.error('Error setting up recovery session:', error);
        setTokenValid(false);
      }
    };

    checkResetToken();
  }, []);

  useEffect(() => {
    // Calculate password strength
    const calculatePasswordStrength = (password) => {
      const feedback = [];
      let score = 0;

      if (password.length >= 8) {
        score += 1;
      } else {
        feedback.push('Password must be at least 8 characters long');
      }

      if (/[a-z]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain lowercase letters');
      }

      if (/[A-Z]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain uppercase letters');
      }

      if (/[0-9]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain numbers');
      }

      if (/[^A-Za-z0-9]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain special characters');
      }

      setPasswordStrength({ score, feedback });
    };

    if (password) {
      calculatePasswordStrength(password);
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [password]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!password || !confirmPassword) {
        showNotification('Please fill in all fields', 'error');
        return;
      }

      if (password !== confirmPassword) {
        showNotification('Passwords do not match', 'error');
        return;
      }

      if (passwordStrength.score < 3) {
        showNotification('Password is too weak. Please choose a stronger password.', 'error');
        return;
      }

      if (!captchaToken) {
        showNotification('Please complete the captcha', 'error');
        return;
      }

      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error('Password update error:', error);
        throw error;
      }

      console.log('Password updated successfully');
      
      // Update password_set flag in accounts table for tracking
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('accounts')
          .update({ password_set: true })
          .eq('id', user.id);
        console.log('Updated password_set flag for user:', user.id);
      }
      
      // Sign out the user from the recovery session
      // This ensures they don't remain logged in with the recovery session
      await supabase.auth.signOut();
      
      setResetComplete(true);
      showNotification('Password reset successfully! You can now log in with your new password.', 'success');

      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        history.push('/login');
      }, 3000);

    } catch (error) {
      console.error('Password reset error:', error);
      showError(error, 'Failed to reset password. Please try again or request a new reset link.');
      
      // Reset captcha on error
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 2) return 'bg-red-500';
    if (passwordStrength.score <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength.score <= 2) return 'Weak';
    if (passwordStrength.score <= 3) return 'Good';
    return 'Strong';
  };

  if (tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <XCircle className="mx-auto h-16 w-16 text-red-500" />
            <h2 className="mt-6 text-center text-2xl md:text-3xl font-extrabold text-brand">
              Invalid Reset Link
            </h2>
            <p className="mt-2 text-center text-sm text-gray-300">
              This password reset link is invalid or has expired. Please request a new password reset.
            </p>
            <button
              onClick={() => history.push('/login')}
              className="mt-4 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-brand-dark bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-colors duration-200"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
            <h2 className="mt-6 text-center text-2xl md:text-3xl font-extrabold text-brand">
              Password Reset Complete!
            </h2>
            <p className="mt-2 text-center text-sm text-gray-300">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <p className="mt-2 text-center text-xs text-gray-400">
              Redirecting to login page...
            </p>
            <button
              onClick={() => history.push('/login')}
              className="mt-4 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-brand-dark bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-colors duration-200"
            >
              Go to Login
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-dark py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <img
            src="/Logo2.png"
            alt="Company Logo"
            className="mx-auto h-24 md:h-36 w-auto"
          />
          <h2 className="mt-6 text-center text-2xl md:text-3xl font-extrabold text-brand">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            Enter your new password below
          </p>
        </motion.div>

        <motion.form 
          className="mt-8 space-y-6" 
          onSubmit={handleResetPassword}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="password" className="sr-only">New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-brand-dark" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 pr-10 border border-brand placeholder-gray-500 text-brand-dark rounded-t-md focus:outline-none focus:ring-brand focus:border-brand focus:z-10 sm:text-sm bg-white"
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-brand-dark" />
                  ) : (
                    <Eye className="h-5 w-5 text-brand-dark" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="sr-only">Confirm New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-brand-dark" />
                </div>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 pr-10 border border-brand placeholder-gray-500 text-brand-dark rounded-b-md focus:outline-none focus:ring-brand focus:border-brand focus:z-10 sm:text-sm bg-white"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-brand-dark" />
                  ) : (
                    <Eye className="h-5 w-5 text-brand-dark" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Password Strength Indicator */}
          {password && (
            <div className="mt-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-300">Password Strength:</span>
                <span className={`text-sm font-medium ${
                  passwordStrength.score <= 2 ? 'text-red-500' :
                  passwordStrength.score <= 3 ? 'text-yellow-500' : 'text-green-500'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getPasswordStrengthColor()}`}
                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                ></div>
              </div>
              {passwordStrength.feedback.length > 0 && (
                <div className="mt-2">
                  <ul className="text-xs text-gray-400 space-y-1">
                    {passwordStrength.feedback.map((feedback, index) => (
                      <li key={index}>• {feedback}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Password Match Indicator */}
          {confirmPassword && (
            <div className="flex items-center">
              {password === confirmPassword ? (
                <div className="flex items-center text-green-500">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm">Passwords match</span>
                </div>
              ) : (
                <div className="flex items-center text-red-500">
                  <XCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm">Passwords do not match</span>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-center">
            <HCaptcha
              sitekey={window.REACT_APP_HCAPTCHA_SITE_KEY || process.env.REACT_APP_HCAPTCHA_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
              ref={captchaRef}
              size={isMobile ? 'compact' : 'normal'}
            />
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => history.push('/login')}
              className="font-medium text-brand hover:text-brand-hover focus:outline-none focus:underline transition ease-in-out duration-150 flex items-center"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Login
            </button>
          </div>

          <div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-brand-dark bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-colors duration-200"
              disabled={loading || !captchaToken || password !== confirmPassword || passwordStrength.score < 3}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </motion.button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
